"use client";

import {
	isActionScoped,
	PERMISSION_GROUPS,
	type PermissionRecord,
	type PermissionResource,
	permissionStatement,
} from "@repo/auth/permissions";
import { Checkbox } from "@ui/components/checkbox";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@ui/components/select";

const RESOURCE_LABELS: Record<PermissionResource, string> = {
	organization: "Retailer profile",
	member: "Team members",
	invitation: "Invitations",
	ac: "Roles",
	webhooks: "Webhooks",
	apiKeys: "API keys",
	audit: "Audit log",
	billing: "Billing",
	connections: "Integrations",
};

const GROUP_LABELS: Record<string, string> = {
	organization: "Retailer and team",
	integrations: "Integrations",
	insights: "Activity",
	billing: "Billing",
};

const ACTION_LABELS: Record<string, string> = {
	create: "Create",
	read: "View",
	update: "Edit",
	delete: "Delete",
	cancel: "Cancel",
	view: "View",
	manage: "Manage",
	export: "Export",
	import: "Import",
	assign: "Assign",
	unassign: "Unassign",
	"manage-links": "Manage links",
	"update-config": "Change settings",
};

const SCOPE_LABELS = { all: "Everyone's", own: "Only their own" };

/** Which actions a role may take, grouped by what they act on. */
export function RolePermissionsGrid({
	value,
	onChange,
	disabled,
}: {
	value: PermissionRecord;
	onChange: (permissions: PermissionRecord) => void;
	disabled?: boolean;
}) {
	const actionsOf = (resource: PermissionResource) =>
		(value[resource] ?? []) as string[];

	const has = (resource: PermissionResource, action: string) => {
		const actions = actionsOf(resource);
		return actions.includes(action) || actions.includes(`${action}:own`);
	};

	const scopeOf = (resource: PermissionResource, action: string) =>
		actionsOf(resource).includes(`${action}:own`) ? "own" : "all";

	const write = (resource: PermissionResource, actions: string[]) => {
		const next: PermissionRecord = { ...value };
		if (actions.length > 0) {
			(next[resource] as string[]) = actions;
		} else {
			delete next[resource];
		}
		onChange(next);
	};

	const without = (resource: PermissionResource, action: string) =>
		actionsOf(resource).filter(
			(a) => a !== action && a !== `${action}:own`,
		);

	return (
		<div className="flex flex-col gap-6">
			{Object.entries(PERMISSION_GROUPS).map(([groupKey, group]) => (
				<fieldset key={groupKey} className="flex flex-col gap-2">
					<legend className="mb-2 font-medium text-muted-foreground text-xs">
						{GROUP_LABELS[groupKey] ?? group.label}
					</legend>
					<div className="flex flex-col divide-y divide-border/70 rounded-2xl ring-1 ring-foreground/6 dark:ring-foreground/10">
						{group.resources.map((resource) => {
							const key = resource as PermissionResource;
							const actions = (
								permissionStatement[
									resource as keyof typeof permissionStatement
								] as readonly string[]
							).filter((action) => !action.endsWith(":own"));
							return (
								<div
									key={resource}
									className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start sm:gap-6"
								>
									<p className="shrink-0 font-medium text-sm sm:w-36 sm:pt-1">
										{RESOURCE_LABELS[key]}
									</p>
									<div className="flex flex-1 flex-wrap gap-x-4 gap-y-2">
										{actions.map((action) => {
											const id = `${resource}-${action}`;
											const enabled = has(key, action);
											const scoped = isActionScoped(
												resource,
												action,
											);
											return (
												<div
													key={action}
													className="flex min-h-7 items-center gap-2"
												>
													<Checkbox
														id={id}
														checked={enabled}
														disabled={disabled}
														onCheckedChange={() =>
															write(
																key,
																enabled
																	? without(
																			key,
																			action,
																		)
																	: [
																			...actionsOf(
																				key,
																			),
																			action,
																		],
															)
														}
													/>
													<label
														htmlFor={id}
														className="text-sm"
													>
														{ACTION_LABELS[
															action
														] ?? action}
													</label>
													{scoped && enabled ? (
														<Select
															items={SCOPE_LABELS}
															value={scopeOf(
																key,
																action,
															)}
															disabled={disabled}
															onValueChange={(
																scope,
															) =>
																write(key, [
																	...without(
																		key,
																		action,
																	),
																	scope ===
																	"own"
																		? `${action}:own`
																		: action,
																])
															}
														>
															<SelectTrigger
																size="sm"
																aria-label={`Whose ${RESOURCE_LABELS[key].toLowerCase()} they can ${(ACTION_LABELS[action] ?? action).toLowerCase()}`}
																className="text-xs"
															>
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																<SelectItem value="all">
																	Everyone's
																</SelectItem>
																<SelectItem value="own">
																	Only their
																	own
																</SelectItem>
															</SelectContent>
														</Select>
													) : null}
												</div>
											);
										})}
									</div>
								</div>
							);
						})}
					</div>
				</fieldset>
			))}
		</div>
	);
}
